//Helpers
import { sortOn } from 'src/helpers/utilities';
import { addDate, dateFormat } from 'src/helpers/date_helper';
//Types
import type {
	ITempSchemeObj,
	TempScheduleInterval,
	TimeInterval,
} from 'src/types/TemperatureSchemeType';

export const translateIntervals = (temp_scheme_object: ITempSchemeObj): TempScheduleInterval[] => {
	let intervals: TempScheduleInterval[] = [];
	if (temp_scheme_object?.intervals?.length > 0) {
		for (let interval of temp_scheme_object.intervals) {
			const days: string[] = interval?.indailyint?.dows?.match(/\d/g),
				startAt: string[] = interval?.indailyint?.startat?.split(':');
			if (days && startAt) {
				const startDate: number = Date.UTC(
					2023,
					0,
					1,
					Number(startAt[0]),
					Number(startAt[1]),
					0,
					0,
				);
				intervals.push({
					days: days.map((i: string) => (Number(i) == 7 ? 0 : Number(i))).sort(),
					time: [
						dateFormat(startDate, 'H:i', true),
						dateFormat(addDate(startDate, 'i', interval.indailyint.dur), 'H:i', true),
					],
					dur: interval.indailyint.dur,
					base: interval.tschem_temp,
					disabled: interval.disabled || false,
				});
			}
		}
	}
	return intervals;
};

export const isValidIntervals = (intervals: TempScheduleInterval[]): boolean => {
	const timeline: TimeInterval[] = createTimeline(intervals);
	let prev: TimeInterval = timeline[0];
	let result: boolean = false;
	for (let i = 1; i < timeline.length; ++i) {
		const current: TimeInterval = timeline[i];
		if (current.start < prev.start) {
			result = current.end > prev.start;
		} else {
			result = current.start < prev.end;
		}
		if (result) break;
		prev = current;
	}
	return !result;
};

export const findEmptyInterval = (
	intervals: TempScheduleInterval[],
	base: TempScheduleInterval['base'] = 0,
): TempScheduleInterval => {
	if (intervals.length == 0) {
		const time: string[] = ['08:00', '18:00'];
		return {
			days: [0, 1, 2, 3, 4, 5, 6],
			dur: timeToInterval(time).dur,
			time,
			base,
		};
	}
	const timeline: TimeInterval[] = createTimeline(intervals);
	const timelineLength = timeline.length;
	const oneDay: number = 24 * 60;
	const oneDayMs: number = oneDay * 60 * 1000;
	const emptyTempScheduleIntervals: TempScheduleInterval[] = [];
	let emptyIntervals: TimeInterval[] = [];
	let prevInterval: TimeInterval = {
		start: null,
		end: addDate(timeline[timelineLength - 1].end, 'w', -1),
	};
	for (let i = 0; i < timelineLength; ++i) {
		const currentInterval: TimeInterval = timeline[i];
		if (prevInterval.end < currentInterval.start) {
			emptyIntervals.push({
				start: prevInterval.end,
				end: currentInterval.start,
			});
		}
		prevInterval = currentInterval;
	}
	for (let i = 0; i < emptyIntervals.length; ++i) {
		const interval: TimeInterval = emptyIntervals[i];
		const startTime: string = dateFormat(interval.start, 'H:i');
		const startEnd: string = dateFormat(interval.end, 'H:i');
		const dur: number = interval.end.getTime() - interval.start.getTime();
		const days: number = dur / oneDayMs;
		let day: number = interval.start.getDay();
		const tempScheduleIntervals: TempScheduleInterval = {
			days: Array.from({ length: Math.ceil(days) }, () => {
				if (day == 7) day = 0;
				return day++;
			}),
			time: [startTime, startEnd],
			base,
			dur: Math.round(days * oneDay) % oneDay || oneDay,
		};
		const extInterval = emptyTempScheduleIntervals.find(
			(i: TempScheduleInterval) =>
				i.time[0] == tempScheduleIntervals.time[0] && i.dur == tempScheduleIntervals.dur,
		);
		if (extInterval) {
			extInterval.days = [...extInterval.days, ...tempScheduleIntervals.days];
		} else {
			emptyTempScheduleIntervals.push(tempScheduleIntervals);
		}
	}
	return sortOn(emptyTempScheduleIntervals, 'days', true)[0];
};

export const timeToInterval = (time: TempScheduleInterval['time'], date: Date = null): TimeInterval => {
	const startTime: string[] = time[0].split(':');
	const endTime: string[] = time[1].split(':');
	const fixDate: number = date ? date.getTime() : Date.UTC(2023, 0, 1, 0, 0, 0, 0);
	let start: Date = addDate(fixDate, 'h', Number(startTime[0]));
	let end: Date = addDate(fixDate, 'h', Number(endTime[0]));
	start = addDate(start, 'i', Number(startTime[1]), false, false, true);
	end = addDate(end, 'i', Number(endTime[1]), false, false, true);
	if (end <= start) {
		end = addDate(end, 'd', 1);
	}
	return { start, end, dur: (end.getTime() - start.getTime()) / (1000 * 60) };
};

const createTimeline = (intervals: TempScheduleInterval[]): TimeInterval[] => {
	const timeline: TimeInterval[] = [];
	for (let i = 0; i < intervals.length; ++i) {
		const interval: TempScheduleInterval = intervals[i];
		const days: number[] = interval.days.slice();
		const fixDate: number = Date.UTC(2023, 0, 1, 0, 0, 0, 0);
		for (let j = 0; j < days.length; ++j) {
			const day: number = days[j];
			const timelineInterval = timeToInterval(interval.time, addDate(fixDate, 'd', day));
			timeline.push(timelineInterval);
		}
	}
	return sortOn(timeline, 'start');
};
